import * as React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Mail, Phone, MapPin, BookOpen, Calendar } from 'lucide-react';
import useFormData from '@/hooks/useFormData';
import {
  useSaveEnquiryMutation,
  useEditEnquiryMutation,
} from '../store/enquiryApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { toast } from 'sonner';
import { DialogHeader } from './ui/dialog';
import { Input } from './ui/input';

import { useGetAllCourseQuery } from '@/store/courseApi';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';

const AddEnquiry = ({ enquiry }) => {
  const [SubmitEnquiryFn, { isSuccess, isError }] = useSaveEnquiryMutation();
  const [updateEnq] = useEditEnquiryMutation();
  const { data: courses } = useGetAllCourseQuery();
  const { user } = useSelector((state: RootState) => state.user);

  const { formData, handleChange, resetForm } = useFormData(
    enquiry || {
      Enquirymode: '',
      address: '',
      contact: '',
      course: '',
      date: '',
      mailId: '',
      mode: '',
      name: '',
      qualification: '',
      ref: '',
      remark: '',
    }
  );

  React.useEffect(() => {
    if (isSuccess) {
      toast.success('Enquiry Submitted Success');
      resetForm();
    }
    if (isError) {
      toast.error('Something happens wrong');
    }
  }, [isSuccess, isError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (enquiry) {
        await updateEnq({
          id: enquiry._id,
          Token: user?.token,
          enquiryData: formData,
        });
        toast.success('Update Successfully');
        resetForm();
      } else {
        return await SubmitEnquiryFn({
          Token: user?.token,
          enquiryData: formData,
        });
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  return (
    <div className="w-full mx-auto p-4 space-y-6">
      {/* {/ Existing Enquiry Form /} */}
      <Card className="shadow-lg">
        <CardHeader className="border-b">
          <CardTitle className="text-2xl font-bold">
            <DialogHeader>Enquiry Information</DialogHeader>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-foreground flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </label>
                <Input
                  type="email"
                  id="mailId"
                  value={formData.mailId}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  id="address"
                  onChange={handleChange}
                  value={formData.address}
                  required
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  id="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Qualification <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  id="qualification"
                  value={formData.qualification}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="course"
                  className="text-sm font-medium text-foreground"
                >
                  Interested Course <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.course}
                  onValueChange={(value) =>
                    handleChange({ id: 'course', value })
                  }
                >
                  <SelectTrigger id="course" className="w-full">
                    <SelectValue placeholder="Select Course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses?.map((c) => (
                      <SelectItem key={c._id} value={c.course}>
                        {c.course}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="Enquirymode"
                  className="text-sm font-medium text-foreground"
                >
                  EnquiryMode <span className="text-red-500">*</span>
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleChange({ id: 'Enquirymode', value })
                  }
                  value={formData.Enquirymode}
                >
                  <SelectTrigger id="Enquirymode" className="w-full">
                    <SelectValue placeholder="Select Enquiry mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Office">Office</SelectItem>
                    <SelectItem value="Call">Call</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="mode"
                  className="text-sm font-medium text-foreground"
                >
                  Course Mode <span className="text-red-500">*</span>
                </Label>
                <Select
                  onValueChange={(value) => handleChange({ id: 'mode', value })}
                  value={formData.mode}
                >
                  <SelectTrigger id="mode" className="w-full">
                    <SelectValue placeholder="Select Mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-foreground" />
                  Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  id="date"
                  value={
                    formData.date
                      ? new Date(formData.date).toLocaleDateString('en-CA')
                      : ''
                  }
                  onChange={handleChange}
                  required
                  className="w-full p-2 border text-foreground rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Reference
                </label>
                <Input
                  type="text"
                  id="ref"
                  value={formData.ref}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="remark"
                className="text-sm font-medium text-foreground"
              >
                Remark
              </Label>
              <Textarea
                id="remark"
                onChange={handleChange}
                value={formData.remark}
                rows={3}
                className="w-full"
                placeholder="Enter your remark here"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="submit"
                className="bg-primary text-white transition-colors"
              >
                {enquiry ? 'Update Enquiry' : 'Submit Enquiry'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEnquiry;
